import { defaultHighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { diff } from "@codemirror/merge";
import { EditorState as CodeMirrorState, Compartment } from "@codemirror/state";
import { EditorView, lineNumbers } from "@codemirror/view";
import { CodeMirror, CodeMirrorEditor, react } from "@handlewithcare/react-codemirror";
import {
	type NodeViewComponentProps,
	useEditorEventCallback,
	useEditorState,
	useIsNodeSelected,
} from "@handlewithcare/react-prosemirror";
import { HoverCardPortal } from "@radix-ui/react-hover-card";
import { xcodeDark, xcodeLight } from "@uiw/codemirror-theme-xcode";
import cx from "classnames";
import { NodeSelection } from "prosemirror-state";
import { forwardRef, useEffect, useMemo, useState } from "react";

import { DifferenceOutlineIcon, OpenInFullIcon } from "@moment/icons";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
	IconButton,
	cn,
	inlineLinkChipClassName,
	useTheme,
} from "@moment/ui";

import { getLanguageExtension } from "../codeBlock/languageExtensions";
import { useCodeSnippet } from "./useCodeSnippet";

const themeCompartment = new Compartment();

// Build extensions similar to CodeBlock.tsx but simplified for read-only
const buildExtensions = (language: string, startLine: number) => {
	const languageExtension = getLanguageExtension(language);

	return [
		themeCompartment.of([]),
		syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
		languageExtension,
		lineNumbers({
			formatNumber: (lineNo) => String(startLine + lineNo - 1),
		}),
		EditorView.editable.of(false),
		EditorView.lineWrapping,
		react,
	];
};

export const LinkCodeSnippet = forwardRef<HTMLSpanElement, NodeViewComponentProps>(
	function LinkCodeSnippet({ nodeProps, children: _, ...props }, ref) {
		const { node, getPos } = nodeProps;
		const isNodeSelected = useIsNodeSelected();
		const editorState = useEditorState();
		const [isHoverCardOpen, setIsHoverCardOpen] = useState(false);

		// Also check if the node is within a text selection
		const pos = getPos();
		const { from, to } = editorState.selection;
		const isInTextSelection = from < pos + 1 && to > pos;

		const isSelected = isNodeSelected || isInTextSelection;

		useEffect(() => {
			// IMPORTANT: `useEffect` that updates `isSelected` is much easier to manage than
			// `open={isHoverCardOpen || isSelected}`. This way, things like the escape key will
			// reset the hover card open state, while in the other model they will stay open.
			setIsHoverCardOpen(isNodeSelected);
		}, [isNodeSelected]);

		const url = node.attrs["url"] as string;
		const owner = node.attrs["owner"] as string;
		const repo = node.attrs["repo"] as string;
		const refspec = node.attrs["refspec"] as string;
		const filePath = node.attrs["filePath"] as string;
		const startLine = node.attrs["startLine"] as number;
		const endLine = (node.attrs["endLine"] as number | null) ?? null;

		const onClick = useEditorEventCallback((view) => {
			view.dispatch(
				view.state.tr.setSelection(NodeSelection.create(view.state.doc, getPos()))
			);
		});

		const fileName = filePath.split("/").pop() || filePath;
		const lineText = endLine && endLine > 0 ? `L${startLine}-L${endLine}` : `L${startLine}`;

		const { lines, isLoading, error } = useCodeSnippet(
			owner,
			repo,
			refspec,
			filePath,
			startLine,
			endLine
		);

		const theme = useTheme();

		// Determine language from file extension
		const language = useMemo(() => {
			const ext = filePath.split(".").pop()?.toLowerCase() || "";
			return ext;
		}, [filePath]);

		// Create CodeMirror state - similar to CodeBlock.tsx
		const [codeMirrorState, setCodeMirrorState] = useState(() =>
			CodeMirrorState.create({
				doc: "",
				extensions: buildExtensions(language, startLine),
			})
		);

		// Update content when lines change - similar to CodeBlock.tsx
		const incomingCode = !isLoading && !error && lines.length > 0 ? lines.join("\n") : "";
		if (incomingCode !== codeMirrorState.doc.toString()) {
			setCodeMirrorState((prev) => {
				const current = prev.doc.toString();
				const diffed = diff(current, incomingCode);

				return prev.update({
					changes: diffed.map((change) => ({
						from: change.fromA,
						to: change.toA,
						insert: incomingCode.slice(change.fromB, change.toB),
					})),
				}).state;
			});
		}

		// Update theme when it changes - similar to CodeBlock.tsx
		const codeMirrorTheme = themeCompartment.get(codeMirrorState);
		const effects = [];

		if (theme.resolvedTheme === "light" && codeMirrorTheme !== xcodeLight) {
			effects.push(themeCompartment.reconfigure(xcodeLight));
		} else if (theme.resolvedTheme !== "light" && codeMirrorTheme !== xcodeDark) {
			effects.push(themeCompartment.reconfigure(xcodeDark));
		}

		if (effects.length) {
			setCodeMirrorState(
				(prev) =>
					prev.update({
						effects,
					}).state
			);
		}

		return (
			<HoverCard openDelay={200} open={isHoverCardOpen} onOpenChange={setIsHoverCardOpen}>
				<HoverCardTrigger asChild>
					<span
						ref={ref}
						contentEditable={false}
						suppressContentEditableWarning
						{...props}
						className={cx(
							inlineLinkChipClassName({
								interactive: true,
								selected: isSelected,
							}),
							// IMPORTANT: When you select a range that includes a PR, don't use the
							// blue selection background for the text inside this node view. Use the
							// selection ring below instead to render selection.
							"[&::selection]:!bg-transparent",
							"[&::selection]:![color:currentColor]",
							"[&_*::selection]:!bg-transparent",
							"[&_*::selection]:![color:currentColor]"
						)}
						onClick={onClick}
					>
						<DifferenceOutlineIcon className="text-palette-gray-10" />
						<span className="text-sm font-normal text-primary">
							{fileName}
							<span className="text-secondary">
								{startLine > 0 ? `#${lineText}` : ""}
							</span>
						</span>
					</span>
				</HoverCardTrigger>
				{/* IMPORTANT: If you don't use a portal, using keynav to select the node will
				    result in the contents of the hover card being selected, which is ugly and not
				    what we want. */}
				<HoverCardPortal>
					<HoverCardContent className="max-w-128">
						<div className="flex flex-col items-start gap-2 -mb-4 -mt-4 -mx-4">
							<div className="flex items-center gap-2 w-full px-4 pb-1">
								<div className="flex flex-col items-start gap-1 pt-4 pb-1 w-full">
									<div className="text-xs text-secondary truncate w-full">
										<a
											href={`https://github.com/${owner}/${repo}`}
											className="text-secondary hover:no-underline hover:text-secondary"
											target="_blank"
											rel="noopener noreferrer"
										>
											{owner}/{repo}
										</a>{" "}
										<span className="text-secondary">@ {refspec}</span>
									</div>
									<div className="text-sm font-medium text-primary break-words w-full">
										<a
											href={url}
											className="text-primary no-underline hover:text-primary hover:underline"
											target="_blank"
											rel="noopener noreferrer"
										>
											{filePath}
											{startLine > 0 ? `#${lineText}` : ""}
										</a>
									</div>
								</div>
								<IconButton
									className="ml-auto -mr-2.5 -mt-5"
									preset="quaternary"
									size="sm"
									borderRadius="default"
									label="Expand code snippet"
									icon={<OpenInFullIcon />}
									onClick={useEditorEventCallback((view, event) => {
										event?.preventDefault?.();
										event?.stopPropagation?.();
										const pos = getPos();
										const $pos = view.state.doc.resolve(pos);
										const nodeSize = view.state.doc.nodeAt(pos)!.nodeSize;

										// If we're in inline content, we need to handle it carefully
										if ($pos.parent.inlineContent) {
											let tr = view.state.tr;

											// Check if there's content after the snippet
											const posAfterNode = pos + nodeSize;
											const $posAfter = tr.doc.resolve(posAfterNode);
											const hasContentAfter =
												$posAfter.parentOffset <
												$posAfter.parent.content.size;

											// Check if there's content before the snippet
											const hasContentBefore = $pos.parentOffset > 0;

											// If snippet is the only content in the paragraph, replace the entire paragraph
											if (!hasContentBefore && !hasContentAfter) {
												const parentPos = $pos.before($pos.depth);
												const parentNode = $pos.node($pos.depth);
												tr.replaceWith(
													parentPos,
													parentPos + parentNode.nodeSize,
													view.state.schema.nodes.code_snippet_block.create(
														{
															...node.attrs,
															expanded: true,
														}
													)
												);
											} else {
												// Split after the snippet if there's content after
												if (hasContentAfter) {
													tr.split(posAfterNode);
												}

												// Split before the snippet if there's content before (map position through previous split)
												if (hasContentBefore) {
													const posBefore = tr.mapping.map(pos);
													tr.split(posBefore);
												}

												// Now replace the snippet with the expanded block version
												// Map the position through any splits
												const finalPos = tr.mapping.map(pos);
												const $finalPos = tr.doc.resolve(finalPos);
												const parentPos = $finalPos.before($finalPos.depth);
												const parentNode = $finalPos.node($finalPos.depth);
												tr.replaceWith(
													parentPos,
													parentPos + parentNode.nodeSize,
													view.state.schema.nodes.code_snippet_block.create(
														{
															...node.attrs,
															expanded: true,
														}
													)
												);
											}
											view.dispatch(tr);
										} else {
											// Not in inline content, just replace
											const tr = view.state.tr.replaceWith(
												pos,
												pos + nodeSize,
												view.state.schema.nodes.code_snippet_block.create({
													...node.attrs,
													expanded: true,
												})
											);
											view.dispatch(tr);
										}
									})}
								/>
							</div>
							<div className="flex items-center gap-2 w-full px-4 pb-4">
								<div className="w-full">
									{isLoading ? (
										<div className="rounded border bg-tertiary p-2">
											<div className="text-xs text-secondary">
												Loading code…
											</div>
										</div>
									) : error ? (
										<div className="rounded border bg-tertiary p-2">
											<div className="text-xs text-secondary">
												Failed to load code
											</div>
										</div>
									) : !isLoading && !error && lines.length > 0 ? (
										<CodeMirror
											state={codeMirrorState}
											extensions={buildExtensions(language, startLine)}
										>
											<div className="rounded border bg-primary overflow-auto max-h-64">
												<CodeMirrorEditor
													className={cn(
														"[&>.cm-editor]:!bg-transparent",
														"[&>.cm-focused]:!outline-none",
														"[&_.cm-editor_.cm-scroller]:font-mono",
														"font-normal text-xs",
														"[&_.cm-line]:leading-[1.4]",
														"[&_.cm-editor_.cm-scroller]:bg-transparent",
														"[&_.cm-editor_.cm-gutters]:bg-primary",
														"[&_.cm-editor_.cm-gutters]:text-tertiary",
														"[&_.cm-editor_.cm-gutters]:border-r-0",
														"[&_.cm-editor_.cm-scroller_.cm-content]:py-2",
														"[&_.cm-editor_.cm-cursor]:hidden"
													)}
													tabIndex={-1}
												/>
											</div>
										</CodeMirror>
									) : null}
								</div>
							</div>
						</div>
					</HoverCardContent>
				</HoverCardPortal>
			</HoverCard>
		);
	}
);
